import { useTranslation } from "react-i18next";
import { Grid, GridColumn, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faArrowRotateLeft, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { GUID } from "@utils/AppConstants";
import { DropDownListItem } from "@utils/Models";
import { OrderRecipePreviewModel } from "@pages/orders/order.models";
import FlintTextBox from "@components/controls/FlintTextBox";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import FlintButton from "@components/controls/FlintButton";
import FlintButton_Cancel from "@components/controls/buttons/FlintButton_Cancel";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import GridPagerZone from "@components/page_components/grid/GridPagerZone";
import { OrderRecipeLookupItemModel } from "@pages/orders/preview/recipes/lookup/recipe-lookup.models";
import { useOrderRecipeLookupPanel } from "@pages/orders/preview/recipes/lookup/useOrderRecipeLookupPanel";
import styles from "./OrderRecipeLookupPanel.module.scss";

export default function OrderRecipeLookupPanel(props: OrderRecipeLookupPanelProps) {
    const { state, handlers, utilities } = useOrderRecipeLookupPanel(props);
    const { t, i18n } = useTranslation();

    return (
        <div className={styles.panel}>
            <div className={styles.filterRow}>
                <div className={"row g-2 align-items-end"}>
                    <div className={"col-12 col-sm-6 col-md-3"}>
                        <div className="block-view">
                            <FlintDropDownList<DropDownListItem>
                                required={true}
                                dataSource={state.inkSerieOptions.get}
                                selectedValue={state.inkSerie.get}
                                onItemSelected={handlers.handleInkSerieChanged}
                                label={t("ORDER_PREVIEW_RecipeLookupFilterInkSeries")} />
                        </div>
                    </div>
                    <div className={"col-12 col-sm-6 col-md-3"}>
                        <div className="block-view">
                            <FlintDropDownList<DropDownListItem>
                                required={true}
                                dataSource={state.substrateOptions.get}
                                selectedValue={state.substrate.get}
                                onItemSelected={handlers.handleSubstrateChanged}
                                label={t("ORDER_PREVIEW_RecipeLookupFilterSubstrate")} />
                        </div>
                    </div>
                    <div className={"col-12 col-sm-6 col-md-3"}>
                        <div className={`block-view ${styles.colorCodeField}`}>
                            <FlintTextBox
                                value={state.colorCode.get}
                                onChange={e => handlers.handleColorCodeChanged(e.value ?? "")}
                                onEnterPressed={() => {
                                    if (utilities.isSearchEnabled) handlers.handleSearchClicked();
                                }}
                                label={t("ORDER_PREVIEW_RecipeLookupFilterColorCode")}
                                maxLength={50} />
                            <span className={styles.colorCodeHelp}
                                  title={t("ORDER_PREVIEW_RecipeLookupColorCodeHelp")}
                                  aria-label={t("ORDER_PREVIEW_RecipeLookupColorCodeHelp")}>
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </div>
                    </div>
                    <div className={"col-12 col-sm-6 col-md-3"}>
                        <div className={"d-flex gap-2"}>
                            <FlintButton themeColor={"primary"}
                                className={"flex-grow-1"}
                                disabled={!utilities.isSearchEnabled}
                                onClick={handlers.handleSearchClicked}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} className={"me-1"} />
                                {t("ORDER_PREVIEW_RecipeLookupSearch")}
                            </FlintButton>
                            <FlintButton themeColor={"secondary"}
                                fillMode={"outline"}
                                title={t("ORDER_PREVIEW_RecipeLookupReset")}
                                aria-label={t("ORDER_PREVIEW_RecipeLookupReset")}
                                onClick={handlers.handleResetClicked}>
                                <FontAwesomeIcon icon={faArrowRotateLeft} />
                            </FlintButton>
                        </div>
                    </div>
                </div>
            </div>

            <FlintLoadingWrapper showLoading={state.isLoading.get || state.isAttaching.get} loadingWrapperClassName={styles.gridZone}>
                <Grid
                    data={state.items.get}
                    dataItemKey={"Guid"}
                    sortable={true}
                    sort={state.sort.get}
                    onSortChange={(e: GridSortChangeEvent) => handlers.handleSortChange(e.sort)}
                    scrollable={"scrollable"}
                    className={styles.grid}
                    style={{ height: "100%" }}>
                    <GridColumn title={t("ORDER_PREVIEW_RecipeLookupColumnRecipe")}
                        field={"CodeGeneratorNumber"}
                        cells={{ data: RecipeCell }} />
                    <GridColumn title={t("ORDER_PREVIEW_RecipeLookupColumnOrigin")}
                        field={"OrderNumber"}
                        cells={{ data: OriginCell }} />
                    <GridColumn title={t("ORDER_PREVIEW_RecipeLookupColumnColourStandard")}
                        field={"ColorCode"}
                        cells={{ data: ColourStandardCell }} />
                    <GridColumn title={t("ORDER_PREVIEW_RecipeLookupColumnCreated")}
                        field={"CreatedOn"}
                        cells={{ data: createCreatedCell(i18n.language) }} />
                    <GridColumn title={""}
                        cells={{
                            data: createAttachCell(
                                handlers.handleAttachClicked,
                                state.alreadyAttachedSet.get,
                                state.isAttaching.get,
                                t
                            ),
                        }} />
                </Grid>
                <GridPagerZone
                    key={state.pagerKey.get}
                    totalItems={state.total.get}
                    defaultPageSize={state.pageSize}
                    pagingSteps={[state.pageSize]}
                    onPageChange={handlers.handlePageChange} />
            </FlintLoadingWrapper>

            <div className={styles.footer}>
                <FlintButton_Cancel onClick={handlers.handleClose} buttonText={t("close")} />
            </div>
        </div>
    );
}

export interface OrderRecipeLookupPanelProps {
    OrderGuid: GUID;
    OrderItemGuid: GUID;
    ColourStandardGuid: GUID;
    InitialInkSerie: DropDownListItem;
    InitialSubstrate: DropDownListItem;
    InitialColorCode: string;
    SubstrateOptions?: DropDownListItem[];
    AlreadyAttachedRecipeGuids?: GUID[];
    OnAttached: (recipe: OrderRecipePreviewModel) => void;
    OnClosed: () => void;
}

function RecipeCell({ dataItem }: { dataItem: OrderRecipeLookupItemModel }) {
    return (
        <td>
            <div className={"fw-semibold"}>{dataItem.CodeGeneratorNumber}</div>
            {dataItem.Description && <div className={"text-secondary small"}>{dataItem.Description}</div>}
        </td>
    );
}

function OriginCell({ dataItem }: { dataItem: OrderRecipeLookupItemModel }) {
    return (
        <td>
            <div className={"fw-semibold"}>{dataItem.OrderNumber}</div>
            <div className={"text-muted small"}>{dataItem.CompanyName}</div>
        </td>
    );
}

function ColourStandardCell({ dataItem }: { dataItem: OrderRecipeLookupItemModel }) {
    return (
        <td>
            <div className={"fw-semibold"}>{dataItem.ColorCode ?? ""}</div>
            {dataItem.ProductCodeName && <div className={"text-muted small"}>{dataItem.ProductCodeName}</div>}
        </td>
    );
}

function createCreatedCell(lang: string) {
    return function CreatedCell({ dataItem }: { dataItem: OrderRecipeLookupItemModel }) {
        const date = new Date(dataItem.CreatedOnUtc);
        const formatted = isNaN(date.getTime()) ? "" : date.toLocaleDateString(lang);
        return (
            <td>
                <div>{formatted}</div>
                {dataItem.CreatedBy && <div className={"text-muted small"}>{dataItem.CreatedBy}</div>}
            </td>
        );
    };
}

export function createAttachCell(
    onAttach: (item: OrderRecipeLookupItemModel) => void,
    alreadyAttached: Set<GUID>,
    isAttaching: boolean,
    t: (key: string) => string
) {
    return function AttachCell({ dataItem }: { dataItem: OrderRecipeLookupItemModel }) {
        const isAlreadyAttached = alreadyAttached.has(dataItem.Guid);
        return (
            <td>
                <FlintButton themeColor={"primary"}
                    fillMode={"outline"}
                    size={"small"}
                    disabled={isAlreadyAttached || isAttaching}
                    title={isAlreadyAttached ? t("ORDER_PREVIEW_RecipeLookupAlreadyAttached") : undefined}
                    onClick={() => onAttach(dataItem)}>
                    {t("ORDER_PREVIEW_RecipeLookupAttach")}
                </FlintButton>
            </td>
        );
    };
}
